// alterRole — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'alter-role' })` op (DSE2 §3.F1).
//
// Per-engine support:
//   - mssql:    ADD MEMBER / DROP MEMBER / WITH NAME (rename)
//   - postgres: RENAME TO / WITH PASSWORD / VALID UNTIL
//   - oracle:   IDENTIFIED BY / NOT IDENTIFIED
//   - mysql:    rejected — use grant/revoke for membership; drop+create to rotate.
//   - sqlite:   rejected — no auth model.

import type { SqlEngine } from '../../../../sql-grammar/model';
import type { DdlEdit, RoleChange } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface AlterRoleArgs {
  readonly roleName: string;
  readonly quoted?: boolean;
  readonly mysqlHost?: string;
  readonly change: RoleChange;
}

export function buildAlterRoleEdit(args: AlterRoleArgs): DdlEdit {
  return {
    op: 'alter-role',
    roleName: args.roleName,
    ...(args.quoted ? { quoted: true } : {}),
    ...(args.mysqlHost !== undefined ? { mysqlHost: args.mysqlHost } : {}),
    change: args.change,
  };
}

export async function commitAlterRole(
  ctx: VqeDdlEditContext,
  args: AlterRoleArgs,
): Promise<void> {
  const edit = buildAlterRoleEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

const ALTER_ROLE_SUPPORTED: ReadonlySet<SqlEngine> = new Set([
  'mssql', 'postgres', 'oracle',
] as const);

export function isAlterRoleSupported(engine: SqlEngine): boolean {
  return ALTER_ROLE_SUPPORTED.has(engine);
}

export function getAlterRoleUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') return 'SQLite has no auth model.';
  if (engine === 'mysql') {
    return 'MySQL ALTER ROLE is rarely used — manage membership via GRANT/REVOKE, '
      + 'or DROP and recreate to rotate.';
  }
  return '';
}

export function isNonEmptyRoleChange(change: RoleChange): boolean {
  return Boolean(
    change.mssqlAddMember
      || change.mssqlDropMember
      || change.mssqlName
      || change.pgsqlRenameTo
      || change.pgsqlPassword
      || change.pgsqlValidUntilText
      || change.oracleIdentifiedBy
      || change.oracleNotIdentified,
  );
}

export function validateAlterRoleForEngine(
  engine: SqlEngine,
  args: AlterRoleArgs,
): string | null {
  if (!isAlterRoleSupported(engine)) {
    return getAlterRoleUnsupportedTooltip(engine);
  }
  if (args.roleName.trim().length === 0) {
    return 'Role name is required.';
  }
  if (!isNonEmptyRoleChange(args.change)) {
    return 'Pick at least one change.';
  }
  if (engine !== 'mssql' && (args.change.mssqlAddMember
      || args.change.mssqlDropMember || args.change.mssqlName)) {
    return `${engine} does not accept mssql-only ALTER ROLE options.`;
  }
  if (engine !== 'postgres' && (args.change.pgsqlRenameTo
      || args.change.pgsqlPassword || args.change.pgsqlValidUntilText)) {
    return `${engine} does not accept PostgreSQL-only ALTER ROLE options.`;
  }
  if (engine !== 'oracle' && (args.change.oracleIdentifiedBy
      || args.change.oracleNotIdentified)) {
    return `${engine} does not accept oracle-only ALTER ROLE options.`;
  }
  if (engine === 'oracle' && args.change.oracleIdentifiedBy
      && args.change.oracleNotIdentified) {
    return 'Pick IDENTIFIED BY OR NOT IDENTIFIED, not both.';
  }
  return null;
}
